import React, { useState, useEffect } from 'react';
import { Phone, MapPin, Linkedin, Twitter, Globe } from 'lucide-react';
import LinkedInWidget from './LinkedInWidget';

const Footer = () => {
  const [companyData, setCompanyData] = useState(null);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    // Fetch company data from API
    const fetchCompanyData = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/company`);
        const data = await response.json();
        if (data.success) {
          setCompanyData(data.data);
        }
      } catch (error) {
        console.error('Error fetching company data:', error);
        // Fallback data
        setCompanyData({
          company: {
            name: 'Blu Digital Technologies',
            description: 'Transforming Government & Enterprise Digital Infrastructure',
            logo_white_url: 'https://customer-assets.emergentagent.com/job_civic-web-pros/artifacts/0vtb8q6d_Blu-white-02.png',
            phone: '855-937-3726',
            office_locations: ['Toronto', 'Miami', 'Montreal'],
            social_links: { linkedin: 'https://www.linkedin.com/company/blucreative' },
            calendly_link: 'https://calendly.com/d/cmg2-shw-h76/blu-exploratory-meeting'
          }
        });
      }
    };

    fetchCompanyData();
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (!companyData) {
    return (
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="animate-pulse">Loading...</div>
        </div>
      </footer>
    );
  }

  const { company } = companyData;

  return (
    <footer className="bg-gray-900 text-white relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-blue-900/20 to-indigo-900/20"></div>
      
      <div className="relative">
        {/* Main footer content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <img 
                  src={company.logo_white_url}
                  alt={company.name}
                  className="h-12 w-auto"
                />
                <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
                  {company.name}
                </span>
              </div>
              
              <p className="text-gray-400 leading-relaxed mb-6 max-w-md">
                {company.description}
              </p>

              <div className="flex space-x-4">
                {company.social_links?.linkedin && (
                  <a 
                    href={company.social_links.linkedin} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center justify-center transition-colors duration-300 group"
                  >
                    <Linkedin size={20} className="text-white group-hover:scale-110 transition-transform" />
                  </a>
                )}
                <a href="#" className="w-10 h-10 bg-blue-500 hover:bg-blue-600 rounded-lg flex items-center justify-center transition-colors duration-300 group">
                  <Twitter size={20} className="text-white group-hover:scale-110 transition-transform" />
                </a>
                <a href="#" className="w-10 h-10 bg-indigo-600 hover:bg-indigo-700 rounded-lg flex items-center justify-center transition-colors duration-300 group">
                  <Globe size={20} className="text-white group-hover:scale-110 transition-transform" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-bold text-white mb-6">Quick Links</h3>
              <ul className="space-y-3">
                <li>
                  <button 
                    onClick={() => scrollToSection('#services')}
                    className="text-gray-400 hover:text-blue-400 transition-colors duration-300"
                  >
                    Services
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('#products')}
                    className="text-gray-400 hover:text-blue-400 transition-colors duration-300"
                  >
                    Products
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('#media')}
                    className="text-gray-400 hover:text-blue-400 transition-colors duration-300"
                  >
                    Media
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('#testimonials')}
                    className="text-gray-400 hover:text-blue-400 transition-colors duration-300"
                  >
                    Success Stories
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('#awards')}
                    className="text-gray-400 hover:text-blue-400 transition-colors duration-300"
                  >
                    Contract Awards
                  </button>
                </li>
              </ul>
            </div>

            {/* Company Links & LinkedIn Widget */}
            <div>
              <h3 className="text-lg font-bold text-white mb-6">Company</h3>
              <ul className="space-y-3 mb-6">
                <li>
                  <a href="#about" className="text-gray-400 hover:text-blue-400 transition-colors duration-300">
                    About
                  </a>
                </li>
                <li>
                  <a href="#careers" className="text-gray-400 hover:text-blue-400 transition-colors duration-300">
                    Careers
                  </a>
                </li>
                <li>
                  <a href="#trade-shows" className="text-gray-400 hover:text-blue-400 transition-colors duration-300">
                    Upcoming Trade Shows
                  </a>
                </li>
              </ul>

              {/* LinkedIn Widget */}
              {company.social_links?.linkedin && (
                <div className="mt-6">
                  <LinkedInWidget 
                    companyUrl={company.social_links.linkedin}
                    variant="default"
                  />
                </div>
              )}
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-lg font-bold text-white mb-6">Contact</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Phone size={18} className="text-blue-400" />
                  <a href={`tel:+1${company.phone?.replace(/\D/g, '')}`} className="text-gray-400 hover:text-blue-400 transition-colors duration-300">
                    {company.phone}
                  </a>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin size={18} className="text-blue-400 mt-1" />
                  <div className="text-gray-400">
                    <p className="font-semibold text-blue-300 mb-2">Office Locations</p>
                    <div className="flex flex-col space-y-1 text-sm">
                      {company.office_locations?.map((location, index) => (
                        <span key={index}>{location}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => window.open(company.calendly_link, '_blank')}
                className="mt-6 w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-3 px-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105"
              >
                Schedule Meeting
              </button>
            </div>
          </div>

          {/* Products showcase - will be populated from products API */}
          <div className="border-t border-gray-800 pt-12 mt-12">
            <h3 className="text-lg font-bold text-white mb-6 text-center">Our Product Suite</h3>
            <div className="text-center text-gray-400">
              <p className="text-sm">Innovative solutions for government and enterprise clients</p>
            </div>
          </div>
        </div>

        {/* Bottom section */}
        <div className="border-t border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col items-center justify-center space-y-4">
              {/* Office locations */}
              <div className="text-center">
                <p className="text-sm text-gray-500 mb-2">Office Locations</p>
                <div className="flex items-center space-x-4 text-gray-400">
                  {company.office_locations?.map((location, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && <span className="text-gray-600">|</span>}
                      <span className="hover:text-blue-400 transition-colors duration-300 cursor-pointer">
                        {location}
                      </span>
                    </React.Fragment>
                  ))}
                </div>
              </div>
              
              <div className="flex flex-col md:flex-row items-center justify-between w-full">
                <div className="text-gray-400 text-sm mb-4 md:mb-0">
                  © {currentYear} {company.name}. All rights reserved.
                </div>
                <div className="flex space-x-6 text-sm">
                  <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors duration-300">
                    Privacy Policy
                  </a>
                  <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors duration-300">
                    Terms of Service
                  </a>
                  <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors duration-300">
                    Accessibility
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;